/**
 * Storedash Toast Notification System
 * Shared toast module for all widgets. Zero dependencies, IIFE pattern.
 *
 * @package Storedash
 * @since 4.2.0
 *
 * Usage:
 *   StoredashToast.success('Product added to cart');
 *   StoredashToast.error('Something went wrong');
 *   StoredashToast.warning('Low stock');
 *   StoredashToast.info('Your cart was updated');
 *   StoredashToast.errorFromResponse(error, { product_name: 'Widget X' });
 */

(function() {
    'use strict';

    var config = window.storedashToast || {};

    // Active toasts for stacking
    var activeToasts = [];

    function getConfig() {
        // Re-read in case localized data arrived after initial parse
        if (!config.messages) {
            config = window.storedashToast || {};
        }
        return config;
    }

    function getDuration(type) {
        var c = getConfig();
        if (c.durations && c.durations[type] !== undefined) {
            return c.durations[type];
        }
        var fallbacks = { success: 3, error: 5, warning: 6, info: 4 };
        return fallbacks[type] || 4;
    }

    function getPosition() {
        return getConfig().position || 'bottom-center';
    }

    function recalcPositions() {
        var offset = 0;
        var pos = getPosition();
        var isTop = pos.indexOf('top') === 0;

        for (var i = 0; i < activeToasts.length; i++) {
            var el = activeToasts[i];
            if (isTop) {
                el.style.top = (24 + offset) + 'px';
            } else {
                el.style.bottom = (24 + offset) + 'px';
            }
            offset += el.offsetHeight + 8;
        }
    }

    function removeToast(el) {
        var idx = activeToasts.indexOf(el);
        if (idx > -1) {
            activeToasts.splice(idx, 1);
        }
        el.classList.remove('storedash-toast-visible');
        el.classList.add('storedash-toast-exit');
        el.addEventListener('animationend', function() {
            if (el.parentNode) el.parentNode.removeChild(el);
            recalcPositions();
        }, { once: true });
        // Fallback removal
        setTimeout(function() {
            if (el.parentNode) el.parentNode.removeChild(el);
            recalcPositions();
        }, 400);
    }

    function show(type, message, overrides) {
        overrides = overrides || {};
        var c = getConfig();
        var position = overrides.position || getPosition();
        var duration = overrides.duration !== undefined ? overrides.duration : getDuration(type);
        var showClose = overrides.showCloseButton !== undefined ? overrides.showCloseButton : (c.showCloseButton || false);

        // Create toast element
        var toast = document.createElement('div');
        toast.className = 'storedash-toast storedash-toast--' + type + ' storedash-toast-' + position;

        // Check if this type has an empty border (no left border needed)
        var appearance = c.appearance && c.appearance[type];
        if (appearance && !appearance.border) {
            toast.classList.add('storedash-toast--no-border');
        }

        // Message (XSS-safe: use textContent)
        var msgSpan = document.createElement('span');
        msgSpan.textContent = message;
        toast.appendChild(msgSpan);

        // Close button
        if (showClose) {
            var closeBtn = document.createElement('button');
            closeBtn.className = 'storedash-toast__close';
            closeBtn.setAttribute('aria-label', 'Close');
            closeBtn.innerHTML = '&times;';
            closeBtn.addEventListener('click', function() {
                clearTimeout(dismissTimer);
                removeToast(toast);
            });
            toast.appendChild(closeBtn);
        }

        document.body.appendChild(toast);
        activeToasts.push(toast);
        recalcPositions();

        // Trigger enter animation
        requestAnimationFrame(function() {
            toast.classList.add('storedash-toast-visible');
        });

        // Auto-dismiss
        var dismissTimer = setTimeout(function() {
            removeToast(toast);
        }, duration * 1000);

        return toast;
    }

    /**
     * Replace placeholders like {product_name} and {stock} in a message template
     */
    function replacePlaceholders(template, context) {
        if (!context) return template;
        return template.replace(/\{(\w+)\}/g, function(match, key) {
            return context[key] !== undefined ? context[key] : match;
        });
    }

    /**
     * Convert WooCommerce notice HTML/entities into readable plain text for toasts.
     * Removes CTA links such as "View cart" so the toast only shows the notice itself.
     */
    function getReadableMessage(message) {
        if (!message) return '';

        var container = document.createElement('div');
        container.innerHTML = String(message);

        var links = container.querySelectorAll('a');
        for (var i = 0; i < links.length; i++) {
            links[i].remove();
        }

        var text = (container.textContent || container.innerText || '')
            .replace(/^error:\s*/i, '')
            .replace(/\s+/g, ' ')
            .trim();

        return text;
    }

    /**
     * Extract stock quantity from error message (works with any language).
     * Looks for patterns like "(1 eftir)", "only 3", "1 remaining", or bare digits near stock context.
     */
    function extractStock(message) {
        // Match "(<digits> " pattern (WooCommerce parenthetical, e.g. "(1 eftir)")
        var m = message.match(/\((\d+)\s+/);
        if (m) return m[1];
        // Match "<digits> left/remaining/available/in stock/eftir"
        m = message.match(/(\d+)\s*(?:left|remaining|available|in stock|eftir)/i);
        if (m) return m[1];
        // Match "we have <digits> in stock"
        m = message.match(/we have\s+(\d+)\s+in stock/i);
        if (m) return m[1];
        // Match "only <digits>"
        m = message.match(/only\s+(\d+)/i);
        if (m) return m[1];
        return null;
    }

    /**
     * Map a WC Store API error to a configured message and show it.
     *
     * Matching strategy:
     * 1. Error CODE first (language-independent, most reliable)
     * 2. Error message content fallback (English only)
     * 3. Generic fallback
     */
    function errorFromResponse(error, context) {
        var c = getConfig();
        var msgs = (c.messages || {});
        var message = getReadableMessage((error && error.message) || '');
        var code = (error && error.code) || '';
        var msgLower = message.toLowerCase();
        var template;
        context = context || {};

        // --- Network errors (no code, JS-level) ---
        if (error instanceof TypeError || msgLower.indexOf('failed to fetch') !== -1) {
            template = msgs.network_error || 'Network error. Please check your connection.';
            return show('error', replacePlaceholders(template, context));
        }

        // --- Match on WooCommerce Store API error CODES (language-independent) ---

        // Session / auth errors
        if (code === 'woocommerce_rest_authentication_error' || (error && error.status === 403)) {
            template = msgs.session_expired || 'Your session has expired. Please refresh the page.';
            return show('error', replacePlaceholders(template, context));
        }

        // Not enough stock (partial out of stock)
        if (code === 'woocommerce_rest_product_partially_out_of_stock' ||
            code === 'woocommerce_rest_product_not_enough_stock') {
            var stock = extractStock(message);
            if (stock) context.stock = stock;
            template = msgs.not_enough_stock || 'Sorry, only {stock} left in stock.';
            return show('error', replacePlaceholders(template, context));
        }

        // Completely out of stock
        if (code === 'woocommerce_rest_product_out_of_stock') {
            template = msgs.out_of_stock || 'This product is currently out of stock.';
            return show('error', replacePlaceholders(template, context));
        }

        // Sold individually
        if (code === 'woocommerce_rest_product_sold_individually') {
            template = msgs.sold_individually || 'You can only have 1 of this product in your cart.';
            return show('error', replacePlaceholders(template, context));
        }

        // Too many in cart (same product, exceeds stock)
        if (code === 'woocommerce_rest_product_too_many_in_cart') {
            var stock2 = extractStock(message);
            if (stock2) context.stock = stock2;
            template = msgs.not_enough_stock || 'Sorry, only {stock} left in stock.';
            return show('error', replacePlaceholders(template, context));
        }

        // Invalid quantity — WC QuantityLimits returns this for sold_individually
        // products (max=1) and other quantity limit violations.
        // The error code comes from WP_Error in QuantityLimits::validate_cart_item_quantity()
        if (code === 'invalid_quantity' || code === 'readonly_quantity') {
            // Try to detect if this is a "max 1" (sold individually) situation
            var maxMatch = message.match(/(?:maximum|max).*?(\d+)/i);
            if (maxMatch && maxMatch[1] === '1') {
                template = msgs.sold_individually || 'You can only have 1 of this product in your cart.';
            } else {
                var stockQ = extractStock(message);
                if (stockQ) context.stock = stockQ;
                template = msgs.not_enough_stock || 'Sorry, only {stock} left in stock.';
            }
            return show('error', replacePlaceholders(template, context));
        }

        // Invalid product / cannot be purchased
        if (code === 'woocommerce_rest_cart_invalid_product' ||
            code === 'woocommerce_rest_cart_product_is_not_purchasable') {
            template = msgs.generic_error || 'Could not add to cart. Please try again.';
            return show('error', replacePlaceholders(template, context));
        }

        // Catch-all for woocommerce_rest_add_to_cart_error (3rd-party validation failures)
        if (code === 'woocommerce_rest_add_to_cart_error') {
            if (message) {
                return show('error', message);
            }
            template = msgs.generic_error || 'Could not add to cart. Please try again.';
            return show('error', replacePlaceholders(template, context));
        }

        // --- Fallback: match on English message content ---

        if (msgLower.indexOf('session') !== -1 || msgLower.indexOf('nonce') !== -1) {
            template = msgs.session_expired || 'Your session has expired. Please refresh the page.';
            return show('error', replacePlaceholders(template, context));
        }

        if (msgLower.indexOf('sold individually') !== -1) {
            template = msgs.sold_individually || 'You can only have 1 of this product in your cart.';
            return show('error', replacePlaceholders(template, context));
        }

        if (msgLower.indexOf('already in') !== -1 && msgLower.indexOf('cart') !== -1) {
            template = msgs.already_in_cart || 'This product is already in your cart.';
            return show('error', replacePlaceholders(template, context));
        }

        if (msgLower.indexOf('not enough stock') !== -1 || msgLower.indexOf('not sufficient') !== -1) {
            var stock3 = extractStock(message);
            if (stock3) context.stock = stock3;
            template = msgs.not_enough_stock || 'Sorry, only {stock} left in stock.';
            return show('error', replacePlaceholders(template, context));
        }

        if (msgLower.indexOf('you already have') !== -1 && msgLower.indexOf('in your cart') !== -1) {
            return show('error', message);
        }

        if (msgLower.indexOf('out of stock') !== -1) {
            template = msgs.out_of_stock || 'This product is currently out of stock.';
            return show('error', replacePlaceholders(template, context));
        }

        // Generic fallback
        if (message) {
            return show('error', message);
        }
        template = msgs.generic_error || 'Could not add to cart. Please try again.';
        return show('error', replacePlaceholders(template, context));
    }

    // Public API
    window.StoredashToast = {
        success: function(message, overrides) { return show('success', message, overrides); },
        error: function(message, overrides) { return show('error', message, overrides); },
        warning: function(message, overrides) { return show('warning', message, overrides); },
        info: function(message, overrides) { return show('info', message, overrides); },
        errorFromResponse: errorFromResponse
    };

})();
